import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { apiClient } from "@/lib/api";

const CartContext = createContext(null);

const GUEST_KEY = "neldaari_cart_guest";
const LEGACY_KEY = "neldaari_cart_v1"; // old shared key (pre account-scoped carts)
const EMPTY = { restaurant: null, items: [] };
const userKey = (uid) => `neldaari_cart_u_${uid}`;

/** Signature d'une ligne : même plat + mêmes options + même note → fusion des quantités */
const lineSig = (i) =>
  `${i.dish_id}|${(i.selected_options || []).map((o) => o.option_id).sort().join(",")}|${i.note || ""}`;

const normalizeLine = (i) => ({
  selected_options: [],
  note: "",
  options_total: 0,
  ...i,
  line_id: i.line_id || i.dish_id,
});

/** Une ligne est exploitable seulement si elle a un plat, un prix numérique et
 * une quantité > 0 — les lignes corrompues/obsolètes cassaient le total (NaN)
 * et bloquaient la commande (422). On les écarte au chargement. */
const isValidLine = (i) =>
  i && i.dish_id && Number.isFinite(Number(i.price)) && Number(i.quantity) > 0;

function readCart(key) {
  try {
    const raw = localStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : null;
    if (!parsed || !Array.isArray(parsed.items)) return EMPTY;
    return { ...parsed, items: parsed.items.map(normalizeLine).filter(isValidLine) };
  } catch {
    return EMPTY;
  }
}

/** Merge the guest cart into the account cart. Same (or no) restaurant → sum
 * quantities; different restaurant → the guest cart (most recent) wins. */
function mergeCarts(account, guest) {
  if (!guest.items.length) return account;
  if (!account.items.length) return guest;
  if (account.restaurant?.restaurant_id !== guest.restaurant?.restaurant_id) return guest;
  const items = account.items.map((i) => ({ ...i }));
  guest.items.forEach((gi) => {
    const ex = items.find((i) => lineSig(i) === lineSig(gi));
    if (ex) ex.quantity += gi.quantity;
    else items.push({ ...gi });
  });
  return { restaurant: account.restaurant || guest.restaurant, items };
}

export function CartProvider({ children }) {
  const { user, loading } = useAuth();
  const [cart, setCart] = useState(EMPTY);
  // Storage key currently backing the cart. null until auth is resolved → no writes.
  const storageKeyRef = useRef(null);
  // Sync serveur active uniquement après le chargement initial du panier du compte
  const syncEnabledRef = useRef(false);
  const syncTimerRef = useRef(null);

  // Load the right cart whenever the authenticated user changes (login / logout / account switch)
  useEffect(() => {
    if (loading) return;
    const uid = user?.user_id || null;
    try {
      // One-time migration of the old shared key: attach it to the logged-in
      // account, or drop it for visitors (fixes carts leaking across logouts).
      const legacy = localStorage.getItem(LEGACY_KEY);
      if (legacy != null) {
        if (uid && !localStorage.getItem(userKey(uid))) localStorage.setItem(userKey(uid), legacy);
        localStorage.removeItem(LEGACY_KEY);
      }
    } catch (e) { void e; }

    if (uid) {
      const key = userKey(uid);
      const merged = mergeCarts(readCart(key), readCart(GUEST_KEY));
      storageKeyRef.current = key;
      setCart(merged);
      try {
        localStorage.setItem(key, JSON.stringify(merged));
        localStorage.removeItem(GUEST_KEY); // guest cart is absorbed by the account
      } catch (e) { void e; }
      // Panier SERVEUR (partagé app mobile ↔ site web pour un même compte) :
      // si le panier local est vide, on récupère celui du compte ; sinon le
      // panier local (plus récent) prend le dessus et sera poussé au serveur.
      apiClient.get("/my/cart").then(({ data }) => {
        if (!merged.items.length && Array.isArray(data.items) && data.items.length) {
          const server = { restaurant: data.restaurant, items: data.items.map(normalizeLine).filter(isValidLine) };
          setCart(server);
          try { localStorage.setItem(key, JSON.stringify(server)); } catch (e) { void e; }
        }
        syncEnabledRef.current = true;
        if (merged.items.length) {
          apiClient.put("/my/cart", { restaurant: merged.restaurant, items: merged.items }).catch(() => {});
        }
      }).catch(() => { syncEnabledRef.current = true; });
    } else {
      // Visitor (or just logged out): only the guest cart — never another account's cart
      storageKeyRef.current = GUEST_KEY;
      setCart(readCart(GUEST_KEY));
    }
  }, [user?.user_id, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist to the ACTIVE key only (account key when logged in, guest key otherwise)
  useEffect(() => {
    if (!storageKeyRef.current) return;
    try { localStorage.setItem(storageKeyRef.current, JSON.stringify(cart)); } catch (e) { void e; }
    // Sync serveur (debounce 800 ms) — comptes connectés uniquement
    if (user?.user_id && syncEnabledRef.current) {
      clearTimeout(syncTimerRef.current);
      syncTimerRef.current = setTimeout(() => {
        apiClient.put("/my/cart", { restaurant: cart.restaurant, items: cart.items }).catch(() => {});
      }, 800);
    }
  }, [cart]); // eslint-disable-line react-hooks/exhaustive-deps

  // À la déconnexion / changement de compte : désactiver la sync
  useEffect(() => {
    if (!user?.user_id) syncEnabledRef.current = false;
  }, [user?.user_id]);

  /** Ajoute un plat. `custom` optionnel : { selected_options, note, quantity }
   * (personnalisation façon Deliveroo). Sans custom → ajout direct (1 clic). */
  const addItem = (restaurant, dish, custom = null) => {
    const selected_options = custom?.selected_options || [];
    const note = (custom?.note || "").trim();
    const quantity = custom?.quantity || 1;
    const options_total = selected_options.reduce((s, o) => s + (Number(o.price_delta) || 0), 0);
    const line = normalizeLine({
      ...dish, quantity, selected_options, note, options_total,
      line_id: `ln_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`,
    });
    setCart((prev) => {
      // If different restaurant, replace cart
      if (prev.restaurant && prev.restaurant.restaurant_id !== restaurant.restaurant_id) {
        return { restaurant, items: [line] };
      }
      const existing = prev.items.find((i) => lineSig(i) === lineSig(line));
      if (existing) {
        return {
          restaurant,
          items: prev.items.map((i) =>
            i.line_id === existing.line_id ? { ...i, quantity: i.quantity + quantity } : i
          ),
        };
      }
      return { restaurant, items: [...prev.items, line] };
    });
  };

  /** Met à jour la personnalisation d'une ligne existante (édition depuis le panier) */
  const updateLine = (line_id, custom) => {
    const selected_options = custom?.selected_options || [];
    const note = (custom?.note || "").trim();
    const options_total = selected_options.reduce((s, o) => s + (Number(o.price_delta) || 0), 0);
    setCart((prev) => ({
      ...prev,
      items: prev.items.map((i) =>
        i.line_id === line_id
          ? { ...i, selected_options, note, options_total, quantity: custom?.quantity || i.quantity }
          : i
      ),
    }));
  };

  const updateQuantity = (line_id, qty) => {
    setCart((prev) => ({
      ...prev,
      items: prev.items
        .map((i) => (i.line_id === line_id ? { ...i, quantity: Math.max(0, qty) } : i))
        .filter((i) => i.quantity > 0),
    }));
  };

  const removeItem = (line_id) => {
    setCart((prev) => ({ ...prev, items: prev.items.filter((i) => i.line_id !== line_id) }));
  };

  const clear = () => setCart({ restaurant: null, items: [] });

  const { subtotal, count } = useMemo(() => {
    let s = 0, c = 0;
    cart.items.forEach((i) => { s += (i.price + (i.options_total || 0)) * i.quantity; c += i.quantity; });
    return { subtotal: s, count: c };
  }, [cart]);

  return (
    <CartContext.Provider value={{ cart, addItem, updateLine, updateQuantity, removeItem, clear, subtotal, count }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
